import { useChannelAdminContext } from 'hooks';
import { BulkAction, RowSelectionState } from 'design-system-web';
import { useDownloadFile } from '../../../../../../hooks/useDownloadFile';
import { generatePath } from 'react-router-dom';
import { routes } from 'lane-shared/config';
import { useTranslation } from 'react-i18next';
import {
  useSpaceOpsSrBulkActionsEnabled,
  useWorkOrderDownloadServiceRequestEnabled,
} from 'react-hooks';
import React from 'react';
import { BulkStatusUpdateAction } from './BulkStatusUpdateAction';
import { BulkTeamsAssignAction } from './BulkTeamsAssignAction';
import { BulkAssigneeAction } from './BulkAssigneeAction';
import { Assignable } from 'domains-work-order-type-contracts';

/** @deprecated Use Assignable instead */
type AdminUser = Assignable;

type BulkActionsOptions = {
  assignableTeams?: AdminUser[];
  assignableUsers?: AdminUser[];
  refetch?: () => void;
};

export function useGetBulkActionsForWorkOrders(
  options: BulkActionsOptions = {}
): BulkAction[] {
  const { assignableTeams = [], assignableUsers = [], refetch } = options;
  type WorkOrderBulkDownloadRequest = {
    serviceRequestIds: string[];
  };

  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  const { downloadBulkFilesFromUrl } =
    useDownloadFile<WorkOrderBulkDownloadRequest>();
  const WORKORDER_SELECTION_LIMIT = 50;

  const isDownloadServiceRequestEnabled =
    useWorkOrderDownloadServiceRequestEnabled();
  const isSpaceOpsSrBulkActionsEnabled = useSpaceOpsSrBulkActionsEnabled();

  const bulkActions: BulkAction[] = [];

  if (isDownloadServiceRequestEnabled) {
    bulkActions.push({
      label: 'web.admin.serviceRequest.bulkActions.downloadWorkOrders',
      onClick: async (rowSelection: RowSelectionState) => {
        const selectedWorkOrderIds = Object.entries(rowSelection)
          .filter(([_, isSelected]) => isSelected)
          .map(([workOrderId]) => workOrderId);

        if (selectedWorkOrderIds.length > WORKORDER_SELECTION_LIMIT) {
          await window.Toast.show(
            t(
              'web.admin.serviceRequest.bulkActions.downloadWorkOrders.limitExceeded',
              {
                workOrderSelectionLimit: WORKORDER_SELECTION_LIMIT,
                selectedWorkOrderCount: selectedWorkOrderIds.length,
              }
            ),
            5000
          );

          return;
        }

        window.Alert.loading({
          title: `${t('web.admin.serviceRequest.bulkActions.downloadWorkOrders.preparingYourZipFile')}`,
          disableCloseOnBackgroundClick: true,
        });

        try {
          const url = `${generatePath(routes.bulkDownloadWorkOrderPdfs, {
            channelId: channel?._id || '',
          })}`;

          await downloadBulkFilesFromUrl({
            url,
            body: { serviceRequestIds: selectedWorkOrderIds },
          });
        } catch {
          await window.Toast.show(
            t('web.admin.serviceRequest.bulkActions.downloadWorkOrders.error')
          );
        }

        setTimeout(() => {
          window.Alert.hide();
        }, 500);
      },
    });
  }

  if (isSpaceOpsSrBulkActionsEnabled) {
    bulkActions.push({
      renderAction: (rowSelection?: RowSelectionState) => {
        return React.createElement(BulkStatusUpdateAction, {
          rowSelection,
          refetch,
        });
      },
    });

    if (assignableTeams.length > 0) {
      bulkActions.push({
        renderAction: (rowSelection?: RowSelectionState) => {
          return React.createElement(BulkTeamsAssignAction, {
            rowSelection,
            assignableTeams,
            refetch,
          });
        },
      });
    }

    if (assignableUsers.length > 0) {
      bulkActions.push({
        renderAction: (rowSelection?: RowSelectionState) => {
          return React.createElement(BulkAssigneeAction, {
            rowSelection,
            assignableUsers,
            refetch,
          });
        },
      });
    }
  }

  return bulkActions;
}
